import { supabase, subscribeToPresence } from '@/lib/supabaseClient';

class PresenceService {
    private updateInterval: NodeJS.Timeout | null = null;
    private unsubscribePresence: (() => void) | null = null;
    private currentUserId: string | null = null;
    private onlineUsersCache: Set<string> = new Set();
    private callbacks: ((onlineUserIds: string[]) => void)[] = [];
    private visibilityChangeHandler: (() => void) | null = null;
    private pageHideHandler: (() => void) | null = null;

    /**
     * Initialize presence tracking for current user
     */
    async initialize(userId: string) {
        if (this.currentUserId === userId && this.unsubscribePresence) {
            return;
        }

        if (this.currentUserId && this.currentUserId !== userId) {
            await this.cleanup();
        }

        this.currentUserId = userId;

        console.log('🟢 Initializing presence for user:', userId);

        this.setupVisibilityListener();

        // Update last_seen only while the tab is visible
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            this.startLastSeenUpdates();
        }

        this.unsubscribePresence = subscribeToPresence((onlineUserIds) => {
            const nextOnlineUsers = new Set(onlineUserIds);

            if (!this.setsEqual(this.onlineUsersCache, nextOnlineUsers)) {
                this.onlineUsersCache = nextOnlineUsers;
                this.notifyCallbacks();
            }
        });

        this.notifyCallbacks();
    }

    private setsEqual(a: Set<string>, b: Set<string>): boolean {
        if (a.size !== b.size) return false;
        for (const item of a) {
            if (!b.has(item)) return false;
        }
        return true;
    }

    private notifyCallbacks() {
        const onlineUserIds = Array.from(this.onlineUsersCache);
        this.callbacks.forEach(callback => callback(onlineUserIds));
    }

    /**
     * Listen for tab visibility changes (analogous to AppState on mobile)
     */
    private setupVisibilityListener() {
        this.removeVisibilityListener();

        if (typeof document === 'undefined') return;

        this.visibilityChangeHandler = () => {
            if (document.visibilityState === 'visible') {
                console.log('📱 Tab visible: Resuming presence tracking');
                this.startLastSeenUpdates();
            } else {
                console.log('📱 Tab hidden: Pausing presence tracking');
                this.stopLastSeenUpdates();
                // Record last activity at the moment the tab was hidden
                this.updateLastSeen();
            }
        };

        this.pageHideHandler = () => {
            this.stopLastSeenUpdates();
        };

        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        window.addEventListener('pagehide', this.pageHideHandler);
    }

    private removeVisibilityListener() {
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }

        if (this.pageHideHandler) {
            window.removeEventListener('pagehide', this.pageHideHandler);
            this.pageHideHandler = null;
        }
    }

    /**
     * Start periodic last_seen updates
     */
    private startLastSeenUpdates() {
        if (!this.currentUserId) return;

        this.stopLastSeenUpdates();

        // Update immediately
        this.updateLastSeen();

        // Then update every 2 minutes
        this.updateInterval = setInterval(() => {
            this.updateLastSeen();
        }, 120000);
    }

    /**
     * Stop periodic last_seen updates
     */
    private stopLastSeenUpdates() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }

    /**
     * Update last_seen timestamp in database
     */
    private async updateLastSeen() {
        if (!this.currentUserId) return;

        try {
            const { error } = await supabase
                .from('users')
                .update({ last_seen: new Date().toISOString() })
                .eq('id', this.currentUserId);

            if (error) {
                console.error('Failed to update last_seen:', error);
            } else {
                console.log('✅ Updated last_seen for:', this.currentUserId);
            }
        } catch (err) {
            console.error('Error updating last_seen:', err);
        }
    }

    /**
     * Check if user is online (last seen < 5 minutes ago)
     */
    isUserOnline(userId: string): boolean {
        return this.onlineUsersCache.has(userId);
    }

    /**
     * Get all online user IDs
     */
    getOnlineUserIds(): string[] {
        return Array.from(this.onlineUsersCache);
    }

    /**
     * Subscribe to presence changes for specific callback
     */
    onPresenceChange(callback: (onlineUserIds: string[]) => void) {
        // Add callback to list
        this.callbacks.push(callback);

        // Call immediately with current state
        callback(Array.from(this.onlineUsersCache));

        // Return unsubscribe function
        return () => {
            const index = this.callbacks.indexOf(callback);
            if (index > -1) {
                this.callbacks.splice(index, 1);
            }
        };
    }

    /**
     * Clean up presence tracking
     */
    async cleanup() {
        console.log('🔴 Cleaning up presence');

        this.removeVisibilityListener();
        this.stopLastSeenUpdates();

        if (this.unsubscribePresence) {
            this.unsubscribePresence();
            this.unsubscribePresence = null;
        }

        // Clear callbacks
        this.callbacks = [];
        this.onlineUsersCache.clear();
        this.currentUserId = null;

        // Keep other app channels intact (chat/notifications etc.)
    }
}

// Helper function to check if user was recently active (last 5 minutes)
export const isRecentlyActive = (lastSeen: string | null): boolean => {
    if (!lastSeen) return false;

    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    return new Date(lastSeen).getTime() > fiveMinutesAgo;
};

// Helper function to format "last seen" text
export const formatLastSeen = (lastSeen: string | null, language: string = 'en'): string => {
    if (!lastSeen) return 'Offline';

    const now = Date.now();
    const lastSeenTime = new Date(lastSeen).getTime();
    const diff = now - lastSeenTime;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (language === 'pl') {
        if (minutes < 1) return 'Przed chwilą';
        if (minutes < 60) return `${minutes} min temu`;
        if (hours < 24) return `${hours}h temu`;
        return `${days}d temu`;
    }

    if (language === 'de') {
        if (minutes < 1) return 'Gerade eben';
        if (minutes < 60) return `vor ${minutes} Min.`;
        if (hours < 24) return `vor ${hours} Std.`;
        return `vor ${days} T.`;
    }

    // English (default)
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
};

// Export singleton instance
export const presenceService = new PresenceService();
